import type {
  ContainerCargoItem,
  ContainerCargoType,
  ContainerOrderLine,
} from "../types/containerOrders";

/**
 * One cargo row while it is being edited.
 *
 * Every value is a string, matching how the container line modal holds its draft:
 * a half-typed "1." is a legitimate thing to have in a box, and parsing on each
 * keystroke would eat the dot. Numbers are only produced on submit.
 *
 * `key` is a client-side identity that survives adding and removing rows —
 * React needs one, and `_id` is absent until the row has been saved once. It is
 * never sent.
 */
export interface CargoRowDraft {
  key: string;
  _id?: string;
  quantity: string;
  type: string;
  weightKg: string;
  volumeM3: string;
  length: string;
  width: string;
  height: string;
  description: string;
  marks: string;
}

const str = (v: string | null | undefined) => v ?? "";
const numStr = (v: number | null | undefined) =>
  typeof v === "number" ? String(v) : "";

let rowCounter = 0;
const nextKey = () => `cargo-${++rowCounter}`;

/** A blank row, ready to type into. */
export function emptyCargoRow(): CargoRowDraft {
  return {
    key: nextKey(),
    quantity: "",
    type: "",
    weightKg: "",
    volumeM3: "",
    length: "",
    width: "",
    height: "",
    description: "",
    marks: "",
  };
}

/** Stored cargo → editable rows. */
export function toCargoRows(line: ContainerOrderLine): CargoRowDraft[] {
  return (line.cargoItems ?? []).map((item) => ({
    key: nextKey(),
    _id: item._id,
    quantity: numStr(item.quantity),
    type: str(item.type),
    weightKg: numStr(item.weightKg),
    volumeM3: numStr(item.volumeM3),
    length: numStr(item.length),
    width: numStr(item.width),
    height: numStr(item.height),
    description: str(item.description),
    marks: str(item.marks),
  }));
}

/** An empty box means "not known", which is null — not zero. */
export const numOrNull = (value: string) =>
  value.trim() === "" || Number.isNaN(Number(value)) ? null : Number(value);

/**
 * The four boxes a cargo row cannot be saved without.
 *
 * The weight is one of them because the container's own weights are checked
 * against this list: the VGM, the tare and the payload rating are all related to
 * each other through what the packing list says is inside, and a row that
 * declares no weight silently contributes nothing to that sum. Two pallets at an
 * unstated weight are not weightless — they are unmeasured, and the difference
 * decides whether an overload is caught before the box leaves the yard or at the
 * terminal weighbridge. Zero is still an answer (an empty crate weighs nothing);
 * blank is not.
 */
export const CARGO_REQUIRED_FIELDS = [
  "quantity",
  "type",
  "weightKg",
  "description",
] as const;

export type CargoRequiredField = (typeof CARGO_REQUIRED_FIELDS)[number];

/**
 * The two required boxes that are figures rather than text.
 *
 * For these, "filled in" is more than "not blank": a count of `-3` or a weight
 * of `abc` is not a partly-typed answer waiting to be finished, it is one the
 * API will refuse (`min(0)` on both, on the validator and on the schema). So the
 * table marks it here rather than letting the save be the first to mention it.
 */
const CARGO_REQUIRED_NUMBERS: readonly CargoRequiredField[] = [
  "quantity",
  "weightKg",
];

/**
 * Which required boxes each row has left empty, keyed by row.
 *
 * *Every* row is checked, including one nobody typed into. A blank row used to
 * be exempt, because submit quietly dropped it — so adding three rows, filling
 * two and saving made the third disappear with nothing said. A row on screen is
 * a row the user put there; if it is not going to be saved they should be the
 * one to remove it. So a blank row is now simply a row missing all four, and
 * comes back marked like any other.
 *
 * Returns an empty object when everything passes, so a caller can test it with
 * `Object.keys(...).length` and the component can look rows up directly.
 */
export function findCargoRowErrors(
  rows: CargoRowDraft[],
): Record<string, CargoRequiredField[]> {
  const errors: Record<string, CargoRequiredField[]> = {};

  for (const row of rows) {
    const missing = CARGO_REQUIRED_FIELDS.filter((field) => {
      const value = row[field].trim();
      if (!value) return true;
      if (!CARGO_REQUIRED_NUMBERS.includes(field)) return false;
      const parsed = Number(value);
      return !Number.isFinite(parsed) || parsed < 0;
    });
    if (missing.length) errors[row.key] = missing;
  }

  return errors;
}

/**
 * What the packing list weighs, in kilos.
 *
 * Rows with no weight count as zero — only reachable for rows stored before the
 * weight was required, since the table now refuses to save one.
 */
export function cargoTotalWeight(rows: CargoRowDraft[]): number {
  return rows.reduce((total, row) => total + (numOrNull(row.weightKg) ?? 0), 0);
}

/**
 * The cargo weight a container may carry, or null while that is unknowable.
 *
 * VGM is the whole box on the weighbridge and tare is the empty box, so what is
 * inside is the difference — and that difference is the ceiling the packing
 * list has to fit under. Either figure missing means there is no ceiling yet: a
 * container that has not been weighed is not an overloaded one.
 *
 * The same arithmetic the line model enforces on save, kept here so the form
 * can say it before the save rather than after.
 */
export function cargoAllowance(
  vgmKg: number | null,
  tareKg: number | null,
): number | null {
  if (vgmKg === null || tareKg === null) return null;
  if (!Number.isFinite(vgmKg) || !Number.isFinite(tareKg)) return null;
  if (vgmKg <= 0 || tareKg < 0) return null;
  return vgmKg - tareKg;
}

/**
 * By how much the packing list overshoots what the container allows, or 0.
 *
 * One helper for all three places that have to say it — the container page's
 * live warning, the cargo totals bar, and the driver's own screen — so the three
 * cannot drift into disagreeing about the same container.
 */
export function cargoOverweightBy(
  cargoKg: number,
  allowanceKg: number | null,
): number {
  if (allowanceKg === null) return 0;
  return cargoKg > allowanceKg ? cargoKg - allowanceKg : 0;
}

/** Which box a weight problem belongs under. */
export type WeightField =
  | "vgmKg"
  | "cargoItems"
  /** The declared load, when one is in force — see `evaluateWeights`. */
  | "cargoWeightKg"
  | "maxPayloadKg";

export interface WeightIssue {
  code:
    /** Two figures the wrong way round — see `evaluateWeights`. */
    | "tareOverPayload"
    | "vgmBelowTare"
    | "cargoOverAllowance"
    /** Same fault, said without a tare to subtract — see `evaluateWeights`. */
    | "cargoOverVgm"
    | "vgmOverPayload"
    | "cargoOverPayload"
    /** A declaration lighter than the rows it has to cover. */
    | "cargoBelowList";
  field: WeightField;
  /** Interpolation values for `lineWeights.<code>` — all already rounded. */
  values: Record<string, string>;
}

export interface WeightState {
  allowanceKg: number | null;
  /** Every way the four weights currently contradict each other. All block. */
  errors: WeightIssue[];
}

const num = (value: number | null | undefined): number | null =>
  value === null || value === undefined || !Number.isFinite(value)
    ? null
    : value;

/**
 * The four weights on a container, checked against each other.
 *
 *   tare        the empty box, off its door plate
 *   maxPayload  what that box is rated to carry, off the same plate
 *   cargo       what the packing list adds up to — derived, never typed
 *   VGM         the whole thing on the weighbridge, as declared to the carrier
 *
 * The first two are facts about the *box*; the other two about this *journey*,
 * measured after loading. Four relationships hold, and all four block the save:
 *
 *   0. tare < maxPayload       no container carries less than it weighs
 *   1. VGM ≥ tare              a loaded box cannot weigh less than an empty one
 *   2. VGM ≤ tare + maxPayload nothing loaded past the container's rating
 *   3. cargo ≤ VGM − tare      the packing list cannot describe more than the
 *                              weighbridge says is inside
 *   4. cargo ≤ maxPayload      the same rating, against the list
 *
 * A max gross used to sit with the first two, and its removal cost the plate
 * its self-check: three figures bound by an exact sum meant any one read wrongly
 * was caught by the other two, before any load. Rule 0 is what remains, and it
 * only catches the pair being the wrong way round.
 *
 * The exact mirror of `evaluateContainerWeights` in the line model, which is the
 * gate all three writers actually pass through. A test puts every combination
 * through both.
 *
 * At most one problem is reported per field, and it is the binding one — the
 * tightest ceiling the cargo actually breaches — so the message names the number
 * that has to move rather than listing every way the set is inconsistent.
 *
 * No cargo rows means 0 kg: an empty container, not an unknown one.
 *
 * The exact mirror of `evaluateContainerWeights` in models/ContainerOrderLine.js,
 * which is the gate all three writers (this page, the driver's screen, the API)
 * actually pass through. This copy exists so a form can say it while it is being
 * typed rather than after a rejected save.
 */
export function evaluateWeights({
  tareKg,
  maxPayloadKg,
  vgmKg,
  cargoKg = 0,
  declaredCargoKg = null,
}: {
  tareKg: number | null;
  maxPayloadKg: number | null;
  vgmKg: number | null;
  /** What the packing list itemises. Keeps its old meaning, so old callers hold. */
  cargoKg?: number;
  /** What the load was weighed at, when it has been. */
  declaredCargoKg?: number | null;
}): WeightState {
  const tare = num(tareKg);
  const payload = num(maxPayloadKg);
  const vgm = num(vgmKg);
  const listed = Number.isFinite(cargoKg) ? cargoKg : 0;

  // Stated with `>= 0`, unlike every other weight here: a *load* can genuinely
  // weigh nothing — an empty repositioning move — so zero is an answer rather
  // than a blank. See `cargoWeightIsStated` in the line model.
  const declared = cargoWeightIsStated(declaredCargoKg)
    ? (declaredCargoKg as number)
    : null;

  /** What is actually inside, as the container now says it. */
  const cargo = declared ?? listed;

  // A message names the number that has to move, so a load problem points at
  // the declaration when there is one and at the table when there is not.
  const cargoField: WeightField =
    declared !== null ? "cargoWeightKg" : "cargoItems";

  const errors: WeightIssue[] = [];

  /** What is actually allowed inside, as a figure to show. Needs both halves. */
  const allowanceKg = cargoAllowance(vgm, tare);

  /**
   * The ceiling the VGM implies, which exists as soon as the VGM does.
   *
   * A missing tare used to take the whole check with it — and that was the hole
   * a 111 kg packing list slipped through under a 10 kg VGM. It need not: a tare
   * is a mass, so it is at least zero, which makes the VGM itself a valid (if
   * generous) upper bound on what can be inside. Standing in zero for an unknown
   * tare therefore never produces a false accusation — it only declines to be
   * blind. The message says which of the two it is talking about.
   */
  const vgmCeiling = vgm !== null && vgm >= 0 ? vgm - (tare ?? 0) : null;

  const kg = (n: number) => Math.round(n).toLocaleString();

  // ── The two plate figures against each other ────────────────────────────
  //
  // One rule, where there used to be four. The max gross has been removed from
  // the platform, and with it the exact sum that let any single mis-read figure
  // be caught by the other two. What is left is the pair being the wrong way
  // round — which is worth keeping precisely because nothing else can see it:
  // no container carries less than it weighs.
  if (tare !== null && payload !== null && tare > 0 && payload > 0 && tare > payload)
    errors.push({
      code: "tareOverPayload",
      field: "maxPayloadKg",
      values: { tare: kg(tare), payload: kg(payload) },
    });

  const vgmBelowTare = vgm !== null && tare !== null && vgm < tare;

  if (vgmBelowTare)
    errors.push({
      code: "vgmBelowTare",
      field: "vgmKg",
      values: { vgm: kg(vgm!), tare: kg(tare!) },
    });
  else if (
    // Only once the VGM is at least the tare: below it, the load it implies is
    // negative and an overload message would be nonsense on top of nonsense.
    payload !== null &&
    vgm !== null &&
    tare !== null &&
    vgm > tare + payload
  )
    errors.push({
      code: "vgmOverPayload",
      field: "vgmKg",
      values: {
        over: kg(vgm - tare - payload),
        limit: kg(tare + payload),
        tare: kg(tare),
        payload: kg(payload),
      },
    });

  // Two ceilings can apply at once — what the weighbridge leaves room for, and
  // what the container is rated to carry. The lower one has to be satisfied, so
  // that is the one reported; naming both would be two ways of saying the load
  // is too heavy, and neither would say by how much it has to come down.
  const ceilings: Array<{ limit: number; code: WeightIssue["code"] }> = [];
  // Skipped when the VGM is below the tare: that contradiction is reported
  // above, and its ceiling is negative — the packing list would be blamed for a
  // mistake made two fields away.
  if (vgmCeiling !== null && !vgmBelowTare)
    ceilings.push({
      limit: vgmCeiling,
      code: tare === null ? "cargoOverVgm" : "cargoOverAllowance",
    });
  if (payload !== null)
    ceilings.push({ limit: payload, code: "cargoOverPayload" });

  const binding = ceilings.reduce<(typeof ceilings)[number] | null>(
    (lowest, next) =>
      lowest === null || next.limit < lowest.limit ? next : lowest,
    null,
  );

  /**
   * A declaration lighter than the list it is supposed to cover.
   *
   * One-way, on purpose. Heavier is ordinary — dunnage, pallets and packaging
   * are inside the box and on nobody's packing list — and lighter is a
   * contradiction the derived figure could never produce.
   */
  if (declared !== null && listed > 0 && declared < listed)
    errors.push({
      code: "cargoBelowList",
      field: "cargoWeightKg",
      values: { declared: kg(declared), listed: kg(listed) },
    });

  if (binding && cargo > binding.limit)
    errors.push({
      code: binding.code,
      field: cargoField,
      values: {
        cargo: kg(cargo),
        over: kg(cargo - binding.limit),
        allowance: kg(binding.limit),
        payload: payload !== null ? kg(payload) : "",
        vgm: vgm !== null ? kg(vgm) : "",
      },
    });

  return { allowanceKg, errors };
}

/** The problem on a given box, if any. */
export function issueFor(
  state: WeightState,
  field: WeightField,
): WeightIssue | undefined {
  return state.errors.find((issue) => issue.field === field);
}

/**
 * Whether a declared cargo weight was actually stated.
 *
 * `>= 0`, where every other weight on a container is read as `> 0`. Elsewhere
 * zero means nobody filled the box in, because no container weighs nothing and
 * no plate is rated for nothing. A load genuinely can, so here zero is an
 * answer. The exact mirror of `cargoWeightIsStated` in the line model.
 */
export const cargoWeightIsStated = (value: number | null | undefined): boolean =>
  value !== null && value !== undefined && Number.isFinite(value) && value >= 0;

/**
 * The VGM this container would be stored with — SOLAS Method 2.
 *
 * `tare + whatever is actually inside`, where "inside" is the weighbridge
 * ticket when there is one and the packing list when there is not. The exact
 * mirror of `deriveVgmKg` in models/ContainerOrderLine.js, which is the only
 * writer: the server recomputes this on every save and ignores anything a
 * client sends for it.
 *
 * ── Why this is a function and not a line of arithmetic ───────────────────
 *
 * Because it was four lines of arithmetic, in four files, and one of them was
 * wrong. The office form, the driver's details panel, the driver's packing
 * list and the plate-import preview each wrote out `tare + cargo` themselves,
 * and the packing-list one reached for the *rows* where the other three reached
 * for the figure in force. On any container that had been weighed, that put the
 * VGM out by the difference between the ticket and the list — which then
 * propagated into `evaluateWeights`, produced an allowance that was not the
 * cargo, and refused perfectly good saves with a rule that cannot otherwise
 * fire.
 *
 * It is a one-line identity and that is exactly what made it dangerous:
 * short enough that nobody extracted it, load-bearing enough that getting it
 * wrong invalidates every weight check downstream of it. One copy, tested
 * against the server's across every combination.
 *
 * Null without a tare, as on the server: a container whose plate has not been
 * read has no gross mass to declare, and showing the bare cargo weight in its
 * place would be a figure that looks like a VGM and is short by the several
 * tonnes the empty box weighs. A negative tare is refused the same way — it is
 * not a mass.
 *
 * @see evaluateWeights — every rule that reads the result
 */
export function deriveVgm({
  tareKg,
  declaredCargoKg,
  listedKg,
}: {
  tareKg: number | null | undefined;
  /** What the load weighed, when it has been weighed. */
  declaredCargoKg: number | null | undefined;
  /** What the packing list adds up to. */
  listedKg: number;
}): number | null {
  const tare =
    tareKg === null || tareKg === undefined || !Number.isFinite(Number(tareKg))
      ? null
      : Number(tareKg);

  if (tare === null || tare < 0) return null;

  return tare + effectiveCargoKg(declaredCargoKg ?? null, listedKg);
}

/**
 * The cargo weight in force — the declaration when there is one, the rows when
 * there is not. Mirrors `cargoKgOf` in the line model.
 */
export const effectiveCargoKg = (
  declaredCargoKg: number | null | undefined,
  listedKg: number,
): number => (cargoWeightIsStated(declaredCargoKg) ? (declaredCargoKg as number) : listedKg);

/**
 * Whatever is wrong with the load, wherever it is being reported.
 *
 * The problem moves between two boxes depending on which cargo figure is in
 * force, and every screen that shows a single "the load is wrong" message wants
 * it regardless. One reader, so the three of them cannot end up watching
 * different fields and disagreeing about the same container.
 */
export const cargoIssueOf = (state: WeightState): WeightIssue | undefined =>
  issueFor(state, "cargoWeightKg") ?? issueFor(state, "cargoItems");

/**
 * Editable rows → the payload the API stores.
 *
 * Nothing is filtered. Every row the user can see is sent, so what comes back
 * is what they left behind; incomplete rows are stopped at the save by
 * `findCargoRowErrors` and marked in place, and the API holds the same rule for
 * anything that does not come through this form.
 */
export function fromCargoRows(rows: CargoRowDraft[]): ContainerCargoItem[] {
  return rows.map((row) => ({
    // Only sent when the row already exists server-side; a new row has no id
    // and the API mints one.
    ...(row._id ? { _id: row._id } : {}),
    quantity: numOrNull(row.quantity),
    type: (row.type || "") as ContainerCargoType | "",
    weightKg: numOrNull(row.weightKg),
    volumeM3: numOrNull(row.volumeM3),
    length: numOrNull(row.length),
    width: numOrNull(row.width),
    height: numOrNull(row.height),
    description: row.description.trim(),
    marks: row.marks.trim(),
  }));
}

/**
 * Editable rows → the four fields the driver's screen is allowed to write.
 *
 * A separate reader from `fromCargoRows`, and not a filter over its output. The
 * full version sends nine fields per row, and a row sent with `volumeM3: null`
 * is a row *claiming* the volume is unknown — so reusing it from the cab would
 * blank a volume, three dimensions and the shipping marks dispatch transcribed
 * off the packing list every time a driver corrected a pallet count at a
 * loading bay. The five absent fields are absent so that the server has nothing
 * to apply, and keeps what is stored (it merges by `_id`).
 *
 * Which also means the two readers cannot drift into the same shape by
 * accident: this one is short because the screen is, and the screen is short
 * because a phone held in one hand at a gate is the wrong instrument for a
 * volume in cubic metres.
 *
 * Nothing is filtered out, exactly as above: every row on screen is sent, and
 * an incomplete one is stopped by `findCargoRowErrors` before the save rather
 * than disappearing into it.
 */
export function fromDriverCargoRows(rows: CargoRowDraft[]): Array<{
  _id?: string;
  quantity: number | null;
  type: string;
  weightKg: number | null;
  description: string;
}> {
  return rows.map((row) => ({
    ...(row._id ? { _id: row._id } : {}),
    quantity: numOrNull(row.quantity),
    type: row.type || "",
    weightKg: numOrNull(row.weightKg),
    description: row.description.trim(),
  }));
}
